import React, { useState, useEffect } from 'react';
import { Modal } from 'bootstrap';
import { toast } from 'react-toastify';
import { formatCurrency, formatDate } from '../../utils/formatters';

interface DueCollection {
  id: string;
  applicationNumber: string;
  customerName: string;
  loanAmount: number;
  dueAmount: number;
  dueDate: string;
  status: 'Due Today' | 'Due Soon' | 'Partially Paid';
  contactNumber: string;
  email: string;
  daysToDue: number;
}

const DueCollectionReminderModal: React.FC = () => {
  const [selectedCollection, setSelectedCollection] = useState<DueCollection | null>(null);
  const [reminderType, setReminderType] = useState<'sms' | 'email'>('sms');
  const [reminderMessage, setReminderMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const modalElement = document.getElementById('DueCollectionReminder');
    if (!modalElement) return;

    // Show event handler
    const handleModalShow = (event: Event) => {
      const collectionDataString = (modalElement as HTMLElement).getAttribute('data-collection-data');
      if (collectionDataString) {
        try {
          const collectionData = JSON.parse(collectionDataString) as DueCollection;
          setSelectedCollection(collectionData);

          // Set default reminder message based on collection data
          const defaultMessage = collectionData.daysToDue === 0 
            ? `Dear ${collectionData.customerName}, your loan payment of ${formatCurrency(collectionData.dueAmount)} is due today. Please ensure that your payment is made on time to avoid any penalties.`
            : `Dear ${collectionData.customerName}, this is a friendly reminder that your loan payment of ${formatCurrency(collectionData.dueAmount)} is due on ${formatDate(collectionData.dueDate)}, which is in ${collectionData.daysToDue} days. Please ensure that your account has sufficient funds.`;
          
          setReminderMessage(defaultMessage);
        } catch (error) {
          console.error('Error parsing collection data:', error);
        }
      }
    };

    // Hidden event handler
    const handleModalHidden = (event: Event) => {
      modalElement.removeAttribute('data-collection-data');
      setSelectedCollection(null);
      setReminderMessage('');
      setReminderType('sms');
      setIsLoading(false);
    };

    // Attach event listeners
    modalElement.addEventListener('shown.bs.modal', handleModalShow);
    modalElement.addEventListener('hidden.bs.modal', handleModalHidden);
    
    // Cleanup
    return () => {
      modalElement.removeEventListener('shown.bs.modal', handleModalShow);
      modalElement.removeEventListener('hidden.bs.modal', handleModalHidden);
    };
  }, []);

  // Handle sending reminder
  const handleSendReminder = () => {
    if (!selectedCollection) return;
    
    setIsLoading(true);
    
    // Simulate API call
    setTimeout(() => {
      setIsLoading(false);
      
      // Close the modal
      const modalElement = document.getElementById('DueCollectionReminder');
      if (modalElement) {
        const modalInstance = Modal.getInstance(modalElement);
        modalInstance?.hide();
      }
      
      toast.success(`${reminderType === 'sms' ? 'SMS' : 'Email'} reminder sent successfully to ${selectedCollection.customerName}`);
    }, 1500);
  };

  return (
    <div className="modal-content rounded-0">
      <div className="modal-header">
        <h5 className="modal-title">Send Payment Reminder</h5>
        <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
      </div>
      <div className="modal-body custom_scroll">
        <form id="reminderForm">
          {/* Summary Section */}
          <div className="card mb-3">
            <div className="card-body p-3">
              <div className="row g-2">
                <div className="col-12">
                  <h6 className="card-title text-muted mb-2">Payment Status Summary</h6>
                </div>
                <div className="col-12 col-md-6">
                  <div className="d-flex align-items-center">
                    <div className="avatar-sm me-3">
                      <div className="avatar-title bg-soft-warning text-warning rounded-circle">
                        <i className="fa fa-calendar-day"></i>
                      </div>
                    </div>
                    <div>
                      <h6 className="text-muted mb-0 small">Due Date</h6>
                      <div className="h6 fw-bold">{selectedCollection ? formatDate(selectedCollection.dueDate) : '-'}</div>
                    </div>
                  </div>
                </div>
                <div className="col-12 col-md-6">
                  <div className="d-flex align-items-center">
                    <div className="avatar-sm me-3">
                      <div className="avatar-title bg-soft-primary text-primary rounded-circle">
                        <i className="fa fa-coins"></i>
                      </div>
                    </div>
                    <div>
                      <h6 className="text-muted mb-0 small">Due Amount</h6>
                      <div className="h6 fw-bold">{selectedCollection ? formatCurrency(selectedCollection.dueAmount) : '-'}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Collection Details Section */}
          <div className="card mb-3">
            <div className="card-body">
              <h6 className="card-title mb-3">Collection Details</h6>
              <div className="form-group mb-3">
                <label className="form-label">Customer Name</label>
                <input
                  type="text"
                  className="form-control"
                  id="customerName"
                  placeholder="Customer Name"
                  value={selectedCollection?.customerName || ''}
                  disabled
                />
              </div>
              <div className="row g-3">
                <div className="col-md-6">
                  <div className="form-group">
                    <label className="form-label">Contact Number</label>
                    <input
                      type="text"
                      className="form-control"
                      value={selectedCollection?.contactNumber || ''}
                      disabled
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <input
                      type="text"
                      className="form-control"
                      value={selectedCollection?.email || ''}
                      disabled
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Reminder Settings Section */}
          <div className="card mb-3">
            <div className="card-body">
              <h6 className="card-title mb-3">Reminder Settings</h6>
              
              <div className="form-group mb-3">
                <label className="form-label">Reminder Type <span className="text-danger">*</span></label>
                <select 
                  className="form-select"
                  value={reminderType}
                  onChange={(e) => setReminderType(e.target.value as 'sms' | 'email')}
                >
                  <option value="sms">SMS to {selectedCollection?.contactNumber}</option>
                  <option value="email">Email to {selectedCollection?.email}</option>
                </select>
              </div>
              
              <div className="form-group mb-3">
                <label className="form-label">Message <span className="text-danger">*</span></label>
                <textarea
                  className="form-control"
                  id="reminderMessage"
                  placeholder="Message"
                  style={{ height: '150px' }}
                  value={reminderMessage}
                  onChange={(e) => setReminderMessage(e.target.value)}
                ></textarea>
              </div>
            </div>
          </div>
          
          <div className="text-center">
            <button 
              type="button" 
              className="btn btn-lg bg-warning w-50 text-light" 
              onClick={handleSendReminder}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Sending...
                </>
              ) : (
                'Send Reminder'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DueCollectionReminderModal; 